import { FormEvent, useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { renderMarkup } from 'react-render-markup'
import { Dispatch } from '@reduxjs/toolkit'
import { Box, Flex } from 'rebass'

import { Select } from '../shared/styledComponents'
import { TdsModalWarning as ModalAutoMedical } from '../shared/TdsModalWarning'
import type { GlobalState } from '../types'
import type { CategorySelectProps, CategorySelectState } from './types'
import { uploadClear,uploadSelectCategory } from './upload-actions'
import {
  categories,
  categoriesAutoAppraisal,
  categoriesMedical,
  roleAutoAppraisal,
  roleMedical
} from './upload-constants'
import { getClaimHandlers, getIdentityValues } from './upload-selectors'

// eslint-disable-next-line max-len
const autoApprHelp = <><p>Have you been instructed to use the Travelers Photo Capture for Auto App to take photos of your vehicle damage? If yes, check for an email from donotreply@cccis.com or text message from 28575 and follow the directions included.</p><p>If you are a shop looking to submit a supplement, please utilize the supplement portal at <a href="https://supplements.travelers.com/">https://supplements.travelers.com/</a></p></>

const CategorySelectComponent = (props: CategorySelectProps) => {
  const {
    claim,
    onSelectCategory,
    identity,
    finished,
    onClear,
    category,
    requesting,
    claimHandlers
  } = props
  const [state, setState] = useState<CategorySelectState>({
    displayNotification: false,
    notificationText: <span />,
    notificationLabel: ''
  })
  const { displayNotification, notificationLabel, notificationText } = state

  const matchRoleCategories = useCallback((category: string, identity: { role: string }) => {
    return claim.lob.toUpperCase() === 'A'
      && categoriesMedical.includes(category)
      && roleMedical.includes(identity.role)
  }, [claim])

  const matchAutoApprRoleCategories =
    useCallback((category: string, identity: { role: string }) => {
      return claim.lob.toUpperCase() === 'A'
        && categoriesAutoAppraisal.includes(category)
        && roleAutoAppraisal.includes(identity.role)
    }, [claim])


  const onCategorySelect = useCallback(
    (event: FormEvent<HTMLSelectElement>) => {
      // format to reflect MM/DD/YYYY format
      const formattedDateOfLost = !claim.dateOfLoss
        ? ''
        : claim.dateOfLoss.slice(0,2) +
          '/' + claim.dateOfLoss.slice(2,4) + '/' +
          claim.dateOfLoss.slice(-4)
      // eslint-disable-next-line max-len
      const medicalHelp = `<p>Medical providers should not use this process.  Use eBill or, for more information on how to submit medical bills or reports for consideration under Auto Medical Payments or Personal Injury Protection coverages, please contact the Provider Inquiry Center at 877-838-7281 or <a href="mailto:providerinquiry@travelers.com?subject=${'Claim Number ' + claim.claim + ' – DOL ' + formattedDateOfLost + ' – Upload Center Inquiry'}">providerinquiry@travelers.com</a>.</p>`
      onSelectCategory(event.currentTarget.value)
      if (matchRoleCategories(event.currentTarget.value, identity)) {
        setState({
          ...state,
          displayNotification: true,
          notificationText: renderMarkup(medicalHelp),
          notificationLabel: 'Auto Medical Warning'
        })
      }

      if (matchAutoApprRoleCategories(event.currentTarget.value, identity)) {
        setState({
          ...state,
          displayNotification: true,
          notificationText: autoApprHelp,
          notificationLabel: 'Auto Appraisal Warning'
        })
      }
    },
    [
      state,
      claim,
      identity,
      onSelectCategory,
      matchAutoApprRoleCategories,
      matchRoleCategories
    ]
  )

  const clearFiles = useCallback(() => {
    if (finished) {
      onClear()
    }
  }, [finished, onClear])

  return (
    <Flex flexWrap='wrap'>
      <Box width={[1, 1 / 3]} >
        <label htmlFor='uploadInformation'>What are you uploading?</label>
      </Box>
      <Box width={[1, 2 / 3]}>
        <Select
          role='listbox'
          id='uploadInformation'
          onClick={clearFiles}
          onChange={onCategorySelect}
          value={category}
          disabled={requesting}
          autoFocus={claimHandlers.length === 0} // eslint-disable-line jsx-a11y/no-autofocus
          data-trigger-modal="warningAuto-modal"
        >
          <option value="">Select Option</option>
          {categories.map((option, index) =>
            <option key={index} value={option.value}>{option.display}</option>)}
        </Select>
        {
          displayNotification ?
            <ModalAutoMedical
              handleOK={() => setState({ ...state, displayNotification: false }) }
              label={notificationLabel}
            >
              {notificationText}
            </ModalAutoMedical>
            : null
        }
      </Box>
    </Flex>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  category: state.upload.category,
  finished: state.upload.finished,
  requesting: state.upload.requesting,
  claimHandlers: getClaimHandlers(state),
  claim: state.claim.claimDetails,
  identity: getIdentityValues(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSelectCategory: (category: string) => dispatch(uploadSelectCategory(category)),
  onClear: () => dispatch(uploadClear()),
})

const CategorySelect = connect(mapStateToProps, mapDispatchToProps)(CategorySelectComponent)

export {
  CategorySelect,
  CategorySelectComponent // for testing without HOC
}
